import React from "react";

interface Timeline {
  quarterStart: string;
  quarterEnd: string;
  cp1?: string | null;
  cp2?: string | null;
  cp3?: string | null;
}

interface Checkpoint {
  CP1Met?: boolean;
  CP2Met?: boolean;
  CP3Met?: boolean;
}

interface Props {
  title: string;
  timeline?: Timeline | null;
  checkpoint?: Checkpoint;
  loading: boolean;
  pacePercentage?: number;
}

const COLORS = {
  success: "bg-green-500",
  warning: "bg-yellow-400",
  danger: "bg-red-500",
  neutral: "bg-gray-300",
};

const formatDate = (date?: string | null) => {
  if (!date) return "";
  const d = new Date(date);
  return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
    .getDate()
    .toString()
    .padStart(2, "0")}/${d.getFullYear().toString().slice(-2)}`;
};

const getPaceColor = (pacePercentage?: number): string => {
  if (pacePercentage == null) return COLORS.neutral;
  if (pacePercentage >= 1.0) return COLORS.success;
  if (pacePercentage >= 0.97) return COLORS.warning;
  return COLORS.danger;
};

const QuarterTimelineCard: React.FC<Props> = ({ title, timeline, checkpoint, loading, pacePercentage }) => {
  const cardBase = "p-4 border border-gray-300 rounded-md bg-white dark:bg-gray-900 dark:border-gray-700";

  if (loading) {
    return (
      <div className={cardBase}>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Loading Timeline...</p>
      </div>
    );
  }

  if (!timeline) {
    return (
      <div className={cardBase}>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-2 text-gray-600 dark:text-gray-400">No Timeline Data Available</p>
      </div>
    );
  }

  const quarterStart = new Date(timeline.quarterStart).getTime();
  const quarterEnd = new Date(timeline.quarterEnd).getTime();
  const today = new Date().getTime();

  const checkpoints = [
    { date: timeline.cp1, met: checkpoint?.CP1Met, label: "Checkpoint 1" },
    { date: timeline.cp2, met: checkpoint?.CP2Met, label: "Checkpoint 2" },
    { date: timeline.cp3, met: checkpoint?.CP3Met, label: "Checkpoint 3" },
  ];

  const isQuarterPast = today > quarterEnd;
  const segments: { fraction: number; color: string }[] = [];
  let prevTime = quarterStart;

  checkpoints.forEach((cp) => {
    if (!cp.date) return;
    const cpTime = new Date(cp.date).getTime();

    if (isQuarterPast) {
      const fraction = (cpTime - prevTime) / (quarterEnd - quarterStart);
      segments.push({ fraction, color: cp.met ? COLORS.success : COLORS.danger });
    } else {
      const pastEnd = Math.min(cpTime, today);
      if (pastEnd > prevTime) {
        const fraction = (pastEnd - prevTime) / (quarterEnd - quarterStart);
        const color = cpTime <= today ? (cp.met ? COLORS.success : COLORS.danger) : getPaceColor(pacePercentage);
        segments.push({ fraction, color });
      }
      if (cpTime > today) {
        const fraction = (cpTime - Math.max(prevTime, today)) / (quarterEnd - quarterStart);
        if (fraction > 0) segments.push({ fraction, color: COLORS.neutral });
      }
    }
    prevTime = cpTime;
  });

  if (prevTime < quarterEnd) {
    const todayFraction = Math.min(today, quarterEnd);
    if (!isQuarterPast && prevTime < todayFraction) {
      segments.push({ fraction: (todayFraction - prevTime) / (quarterEnd - quarterStart), color: getPaceColor(pacePercentage) });
      if (todayFraction < quarterEnd) {
        segments.push({ fraction: (quarterEnd - todayFraction) / (quarterEnd - quarterStart), color: COLORS.neutral });
      }
    } else {
      const fraction = (quarterEnd - prevTime) / (quarterEnd - quarterStart);
      if (fraction > 0) segments.push({ fraction, color: isQuarterPast ? COLORS.success : COLORS.neutral });
    }
  }

  const getSymbol = (cpDate?: string | null, met?: boolean) => {
    if (!cpDate) return "";
    const cpTime = new Date(cpDate).getTime();
    if (cpTime > today) return "❔";
    return met ? "✅" : "❌";
  };

  return (
    <div className={cardBase}>
      <h3 className="font-semibold">{title}</h3>

      <div className="relative mt-4 h-28">
        {/* Quarter Start */}
        <div className="absolute bottom-9 left-0 text-center">
          <div>Quarter Start</div>
          <div className="text-gray-500 text-sm">({formatDate(timeline.quarterStart)})</div>
        </div>

        {/* Quarter End */}
        <div className="absolute bottom-9 right-0 text-center">
          <div>Quarter End</div>
          <div className="text-gray-500 text-sm">({formatDate(timeline.quarterEnd)})</div>
        </div>

        {/* Checkpoints */}
        {checkpoints.map((cp, i) => {
          if (!cp.date) return null;
          const cpTime = new Date(cp.date).getTime();
          const leftPercent = ((cpTime - quarterStart) / (quarterEnd - quarterStart)) * 100;
          return (
            <div
              key={i}
              className="absolute bottom-9 text-center"
              style={{ left: `${leftPercent}%`, transform: "translateX(-50%)" }}
            >
              <div>{cp.label}</div>
              <div className="text-gray-500 text-sm">({formatDate(cp.date)})</div>
              <div className="text-lg">{getSymbol(cp.date, cp.met)}</div>
            </div>
          );
        })}

        {/* Timeline Bar */}
        <div className="absolute bottom-0 left-0 flex w-full h-4 rounded overflow-hidden">
          {segments.map((seg, i) => (
            <div
              key={i}
              className={seg.color}
              style={{ flex: `${seg.fraction} 1 0%`, transition: "background-color 0.3s" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuarterTimelineCard;